import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, deviceApi, photoUrl, kbFileUrl } from "@/api";
import { useRealtime } from "@/hooks/useRealtime";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { BuildingInfo } from "@/components/BuildingInfo";
import { PriorityBadge, StatusBadge } from "@/components/Badges";
import { PRIORITIES, STATUS_ORDER, VEHICLE_STATUSES, timeAgo, ROLE_LABELS } from "@/lib/constants";
import { ShieldCheck, LogOut, Building2, Send, ImagePlus, Loader2, ChevronLeft, ChevronRight, Hourglass, KeyRound, MapPin, Radio, X, Video, AlertTriangle, BookOpen, Camera, Truck, CheckCheck, Ban, FileText } from "lucide-react";
import { IncidentMiniMap } from "@/components/IncidentMaps";
import { WeatherWidget } from "@/components/WeatherWidget";
import { Lightbox } from "@/components/Lightbox";
import { LiveWatch } from "@/components/LiveWatch";
import { LiveBroadcast } from "@/components/LiveBroadcast";
import { VehicleStatusButton } from "@/components/VehicleStatusButton";
import { LogoMark } from "@/context/BrandingContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const devToken = () => localStorage.getItem("deviceToken");

export default function VehicleApp() {
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const [phase, setPhase] = useState("loading"); // loading | enroll | pending | app
  const [vehicle, setVehicle] = useState(null);
  const [user, setUser] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [online, setOnline] = useState(false);
  const [lastMsgAt, setLastMsgAt] = useState(0);
  const [wsStale, setWsStale] = useState(false);
  const wsStatus = lastMsgAt === 0 ? "connecting" : wsStale ? "degraded" : "live";
  const [pos, setPos] = useState(null);
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const scanStopRef = useRef(null);
  const pollRef = useRef(null);

  const loadApp = useCallback(async () => {
    const [me, inc] = await Promise.all([deviceApi.get("/auth/me"), deviceApi.get("/incidents/mine")]);
    setUser(me.data); setVehicle(me.data.vehicle || null); setIncidents(inc.data);
  }, []);

  const startPolling = useCallback((deviceId) => {
    clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/devices/status`, { params: { device_id: deviceId } });
        if (data.status === "approved" && data.token) {
          localStorage.setItem("deviceToken", data.token);
          clearInterval(pollRef.current);
          await loadApp();
          setPhase("app");
        } else if (data.status === "revoked") {
          localStorage.removeItem("deviceToken");
          setPhase("enroll");
          toast.error("Apparaat is ingetrokken");
          clearInterval(pollRef.current);
        }
      } catch (e) {
        if (e.response?.status === 404) { localStorage.removeItem("deviceId"); setPhase("enroll"); clearInterval(pollRef.current); }
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  }, [loadApp]);

  // Initial bootstrap
  useEffect(() => {
    (async () => {
      const urlTok = params.get("token");
      if (urlTok) localStorage.setItem("deviceToken", urlTok);
      if (devToken()) {
        try { await loadApp(); setPhase("app"); return; }
        catch (e) { localStorage.removeItem("deviceToken"); }
      }
      const deviceId = localStorage.getItem("deviceId");
      if (deviceId) { setPhase("pending"); startPolling(deviceId); }
      else setPhase("enroll");
    })();
    return () => clearInterval(pollRef.current);
  }, [loadApp, startPolling]);

  useRealtime((msg) => { if (msg.type === "__ping") { setLastMsgAt(Date.now()); setWsStale(false); return; } setOnline(true); setLastMsgAt(Date.now()); setWsStale(false); { const myEnv = localStorage.getItem("deviceEnvId"); if (msg.payload && msg.payload.env_id && myEnv && msg.payload.env_id !== myEnv) return; } if (msg.type === "incident_created" && msg.payload && vehicle && (msg.payload.vehicle_ids || []).includes(vehicle.id)) { setIncidents((prev) => (prev.some((x) => x.id === msg.payload.id) ? prev : [msg.payload, ...prev])); } if (msg.type === "incident_retracted" && msg.payload && vehicle) { const mine = msg.payload.scope === "all" || (msg.payload.vehicle_ids || []).includes(vehicle.id); if (mine) toast("Melding ingetrokken"); } if (phase === "app") loadApp(); }, devToken());
  useEffect(() => {
    const t = setInterval(() => setWsStale(lastMsgAt > 0 && Date.now() - lastMsgAt > 35000), 5000);
    return () => clearInterval(t);
  }, [lastMsgAt]);
  // Fallback-resync: vangt gemiste WS-events op zodat toegewezen/ingetrokken meldingen snel verschijnen/verdwijnen.
  useEffect(() => {
    if (phase !== "app") return;
    const t = setInterval(() => { loadApp().catch(() => {}); }, 1000);
    return () => clearInterval(t);
  }, [phase, loadApp]);
  useEffect(() => {
    if (!navigator.geolocation) return undefined;
    const id = navigator.geolocation.watchPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {}, { enableHighAccuracy: true, maximumAge: 15000, timeout: 10000 });
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // Installed PWA opens straight to this app (not the command-center login).
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute("href", "/manifest-voertuig.json");
  }, []);

  // Kiosk: ga naar volledig scherm bij de eerste aanraking/klik (browsers vereisen een gebruikersgebaar).
  useEffect(() => {
    const go = () => { const el = document.documentElement; if (el.requestFullscreen && !document.fullscreenElement) el.requestFullscreen().catch(() => {}); };
    window.addEventListener("click", go, { once: true });
    window.addEventListener("touchstart", go, { once: true });
    return () => { window.removeEventListener("click", go); window.removeEventListener("touchstart", go); };
  }, []);

  const doEnroll = async (theCode) => {
    const c = (theCode || "").trim();
    if (!c) return;
    try {
      const existing = localStorage.getItem("deviceId") || undefined;
      const { data } = await axios.post(`${API}/devices/enroll`, { code: c, device_id: existing });
      localStorage.setItem("deviceId", data.device_id);
      setPhase("pending");
      startPolling(data.device_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Koppelen mislukt");
    }
  };
  const enroll = (e) => { if (e && e.preventDefault) e.preventDefault(); doEnroll(code); };

  const parseCode = (raw) => {
    if (!raw) return "";
    try { const u = new URL(raw); const c = u.searchParams.get("code"); if (c) return c.toUpperCase(); } catch (_) {}
    const m = String(raw).match(/[A-Za-z0-9]{4,8}/);
    return (m ? m[0] : String(raw)).toUpperCase();
  };
  const stopScan = () => { setScanning(false); if (scanStopRef.current) { scanStopRef.current(); scanStopRef.current = null; } };
  const startScan = async () => {
    if (!("BarcodeDetector" in window)) { toast.error("Scannen wordt niet ondersteund op dit apparaat — voer de code handmatig in."); return; }
    let stream; let raf; let cancelled = false;
    try { stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } }); }
    catch (e) { toast.error("Geen toegang tot de camera."); return; }
    setScanning(true);
    scanStopRef.current = () => { cancelled = true; if (raf) cancelAnimationFrame(raf); stream.getTracks().forEach((t) => t.stop()); };
    const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
    setTimeout(async () => {
      const v = videoRef.current;
      if (v) { v.srcObject = stream; try { await v.play(); } catch (_) {} }
      const tick = async () => {
        if (cancelled || !videoRef.current) return;
        try {
          const found = await detector.detect(videoRef.current);
          if (found && found.length) {
            const c = parseCode(found[0].rawValue);
            if (c) { setCode(c); stopScan(); toast.success(`Code gescand: ${c}`); doEnroll(c); return; }
          }
        } catch (_) { /* keep scanning */ }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, 100);
  };
  useEffect(() => () => { if (scanStopRef.current) scanStopRef.current(); }, []);

  const setStatus = async (status) => {
    if (!vehicle) return;
    try { const { data } = await deviceApi.post(`/vehicles/${vehicle.id}/status`, { status }); setVehicle(data); toast.success(`Status: ${VEHICLE_STATUSES[status].label}`); }
    catch (err) { toast.error(err.response?.data?.detail || "Mislukt"); }
  };

  const unpair = () => {
    localStorage.removeItem("deviceToken"); localStorage.removeItem("deviceId");
    clearInterval(pollRef.current);
    setPhase("enroll"); setVehicle(null); setUser(null); setIncidents([]);
  };

  // ---- Enrollment screen ----
  if (phase === "loading") return <div className="flex h-screen items-center justify-center bg-slate-900 text-slate-400">Laden…</div>;

  if (phase === "enroll" || phase === "pending") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900 p-6" data-testid="vehicle-enroll">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <LogoMark size={40} rounded="rounded-xl" />
              <div><div className="font-display text-lg font-bold text-white">DispatchMe</div><div className="text-xs uppercase tracking-widest text-slate-500">Voertuig-app</div></div>
            </div>
            <LanguageSwitcher dark />
          </div>

          {phase === "pending" ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-800/50 p-6 text-center" data-testid="vehicle-pending">
              <Hourglass className="mx-auto mb-3 h-8 w-8 animate-pulse text-amber-400" />
              <div className="font-display text-lg font-bold text-white">{t("dev.vehPendingTitle")}</div>
              <p className="mt-1 text-sm text-slate-400">{t("dev.vehPendingHint")}</p>
              <button data-testid="cancel-pending" onClick={unpair} className="mt-4 text-xs font-semibold text-slate-400 hover:text-white">{t("dev.vehCancelPending")}</button>
            </div>
          ) : (
            <form onSubmit={enroll} className="rounded-2xl border border-slate-800 bg-slate-800/50 p-6">
              <div className="flex items-center gap-2 text-sm font-bold text-white"><KeyRound className="h-4 w-4" /> {t("dev.enterCode")}</div>
              <p className="mt-1 text-sm text-slate-400">{t("dev.vehEnrollHint")}</p>
              <input data-testid="enroll-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Bijv. 7GX2QP" maxLength={8}
                className="mt-4 w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-center font-mono text-2xl tracking-widest text-white outline-none focus:border-blue-500" />
              <button data-testid="enroll-submit" type="submit" className="mt-3 w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-500">{t("dev.pairDevice")}</button>
              <button type="button" data-testid="enroll-scan" onClick={startScan} className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 py-3 text-sm font-semibold text-slate-200 hover:bg-slate-800">
                <Camera className="h-4 w-4" /> {t("dev.scanQr")}
              </button>
            </form>
          )}
          {scanning && (
            <div className="fixed inset-0 z-[2000] flex flex-col items-center justify-center bg-black/90 p-6" data-testid="qr-scanner">
              <video ref={videoRef} playsInline muted className="max-h-[60vh] w-full max-w-md rounded-2xl border-2 border-blue-500 object-cover" />
              <p className="mt-4 text-center text-sm text-slate-300">{t("dev.scanAimHint")}</p>
              <button data-testid="qr-scanner-close" onClick={stopScan} className="mt-4 rounded-xl bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20">{t("dev.cancel")}</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- Incident detail ----
  if (selected) return <VehicleIncident incidentId={selected} onBack={() => { setSelected(null); loadApp(); }} vehicle={vehicle} onVehicle={setVehicle} pos={pos} />;

  // ---- Main app ----
  return (
    <div className="min-h-screen bg-slate-100" data-testid="vehicle-app">
      <header className="flex items-center justify-between bg-slate-900 px-5 py-4 text-white">
        <div className="flex items-center gap-2.5">
          <LogoMark size={36} rounded="rounded-lg" />
          <div><div className="font-display text-base font-bold">{vehicle ? vehicle.callsign : "Voertuig"}</div><div className="text-[11px] text-slate-400">{user?.name}</div></div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher dark />
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${wsStatus === "live" ? "bg-emerald-500/20 text-emerald-300" : wsStatus === "degraded" ? "bg-amber-500/20 text-amber-300" : "bg-white/10 text-slate-400"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${wsStatus === "live" ? "bg-emerald-400 live-dot" : wsStatus === "degraded" ? "bg-amber-400 live-dot" : "bg-slate-500"}`} /> {wsStatus === "live" ? "Live" : wsStatus === "degraded" ? "Herverbinden…" : "…"}
          </span>
          <button data-testid="vehicle-unpair" onClick={unpair} className="rounded-lg bg-white/10 p-2" title="Ontkoppelen"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>

      <div className="mx-auto max-w-2xl space-y-5 p-4">
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Eigen status <span className="text-slate-400">· sinds {vehicle ? timeAgo(vehicle.status_since) : "—"}</span></div>
          <VehicleStatusButton vehicle={vehicle} onChanged={setVehicle} />
        </div>

        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Toegewezen incidenten ({incidents.length})</div>
          <div className="space-y-3">
            {incidents.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">Geen toegewezen incidenten</div>}
            {incidents.map((inc) => (
              <button key={inc.id} data-testid={`vehicle-incident-${inc.id}`} onClick={() => setSelected(inc.id)}
                className="block w-full rounded-2xl border-l-4 bg-white p-4 text-left shadow-sm active:scale-[0.99]" style={{ borderLeftColor: PRIORITIES[inc.priority].hex }}>
                <div className="flex items-center gap-2"><PriorityBadge priority={inc.priority} /><span className="font-mono text-xs text-slate-400">{inc.ref}</span></div>
                <div className="mt-1.5 text-lg font-bold text-slate-900">{inc.title}</div>
                <div className="mt-1 flex items-center gap-3 text-sm text-slate-500"><span className="inline-flex items-center gap-1"><Building2 style={{ width: 14, height: 14 }} />{inc.building?.name}</span><span>{inc.incident_type?.name}</span>{inc.subcategory && <span data-testid={`vehicle-list-subcategory-${inc.id}`} className="rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-bold text-indigo-700">{inc.subcategory}</span>}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function VehicleIncident({ incidentId, onBack, vehicle, onVehicle, pos }) {
  const [inc, setInc] = useState(null);
  const [notes, setNotes] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [vlb, setVlb] = useState(null);
  const [liveToken, setLiveToken] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [broadcastForMe, setBroadcastForMe] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentToken, setConsentToken] = useState(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [water, setWater] = useState([]);
  const [sharedLayer, setSharedLayer] = useState(null);
  const [legend, setLegend] = useState(null);
  const [approachEnabled, setApproachEnabled] = useState(true);
  const [channelToken, setChannelToken] = useState(null);
  const [vehBroadcast, setVehBroadcast] = useState(false);
  const [shareMk, setShareMk] = useState(false);
  const [bcaster, setBcaster] = useState(null);
  const myDeviceId = localStorage.getItem("deviceId");
  const [mapMode, setMapMode] = useState("locatie");
  const [procExpanded, setProcExpanded] = useState(null);
  const fileRef = useRef(null);
  const notesEnd = useRef(null);
  const [acked, setAcked] = useState(false);
  const [retracted, setRetracted] = useState(null);
  const [ackPolicy, setAckPolicy] = useState({ require_vehicle_ack: true, require_pager_ack: true });

  const load = useCallback(async () => {
    const [i, n, p, w, la] = await Promise.all([
      deviceApi.get(`/incidents/${incidentId}`), deviceApi.get(`/incidents/${incidentId}/notes`), deviceApi.get(`/incidents/${incidentId}/photos`), deviceApi.get(`/water-points`), deviceApi.get(`/incidents/${incidentId}/live-active`),
    ]);
    setInc(i.data); setNotes(n.data); setPhotos(p.data); setWater(w.data); setLiveToken(la.data.token); setLiveStatus(la.data.status || null); setBroadcastForMe(!!la.data.broadcast_for_me);
  }, [incidentId]);

  useEffect(() => { load(); }, [load]);
  const seenNotes = useRef(null);
  const [flashIds, setFlashIds] = useState({});
  useEffect(() => { seenNotes.current = null; setFlashIds({}); }, [incidentId]);
  useEffect(() => {
    if (seenNotes.current === null) { seenNotes.current = new Set(notes.map((n) => n.id)); return; }
    const fresh = notes.filter((n) => !seenNotes.current.has(n.id));
    if (!fresh.length) return;
    fresh.forEach((n) => seenNotes.current.add(n.id));
    setFlashIds((prev) => { const nx = { ...prev }; fresh.forEach((n) => { nx[n.id] = true; }); return nx; });
    const ids = fresh.map((n) => n.id);
    const to = setTimeout(() => setFlashIds((prev) => { const nx = { ...prev }; ids.forEach((id) => delete nx[id]); return nx; }), 2600);
    return () => clearTimeout(to);
  }, [notes]);
  useEffect(() => {
    const t = setInterval(() => {
      deviceApi.get(`/incidents/${incidentId}/notes`).then(({ data }) => {
        setNotes((prev) => ((data.length !== prev.length || (data.length && prev.length && data[data.length - 1].id !== prev[prev.length - 1].id)) ? data : prev));
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [incidentId]);
  const loadChannel = useCallback(() => { deviceApi.get("/vehicle-live").then(({ data }) => { setChannelToken(data.token); setShareMk(!!data.share_meldkamer); setBcaster(data.broadcaster_device_id ? { id: data.broadcaster_device_id, label: data.broadcaster_label } : null); }).catch(() => {}); }, []);
  useEffect(() => { deviceApi.get("/settings/ack-policy").then(({ data }) => setAckPolicy(data)).catch(() => {}); }, []);
  useEffect(() => { deviceApi.get("/map-layer").then(({ data }) => setSharedLayer(data.enabled !== false ? data.drawings : null)).catch(() => {}); }, []);
  useEffect(() => { deviceApi.get("/settings/color-legend").then(({ data }) => setLegend(data.legend || null)).catch(() => {}); }, []);
  useEffect(() => { deviceApi.get("/settings/map").then(({ data }) => setApproachEnabled(data.approach_routes_enabled !== false)).catch(() => {}); }, []);
  useEffect(() => { loadChannel(); }, [loadChannel]);
  useEffect(() => { const t = setInterval(loadChannel, 6000); return () => clearInterval(t); }, [loadChannel]);
  useEffect(() => { if (!vehBroadcast) return; const t = setInterval(() => { deviceApi.post("/vehicle-live/broadcast", { enabled: true }).catch(() => {}); }, 8000); return () => clearInterval(t); }, [vehBroadcast]);
  const toggleShareMk = async (en) => { setShareMk(en); try { await deviceApi.post("/vehicle-live/meldkamer", { enabled: en, incident_id: incidentId }); } catch (_) {} };
  const startVehBroadcast = async () => {
    try { await deviceApi.post("/vehicle-live/broadcast", { enabled: true }); setVehBroadcast(true); }
    catch (err) { if (err.response?.status === 409) toast.error(err.response.data.detail || "Een andere tablet zendt al uit"); else toast.error("Kon uitzending niet starten"); loadChannel(); }
  };
  const stopVehBroadcast = async () => { setVehBroadcast(false); if (shareMk) toggleShareMk(false); try { await deviceApi.post("/vehicle-live/broadcast", { enabled: false }); } catch (_) {} };
  useRealtime((msg) => {
    if (msg.type === "__ping") return;
    { const myEnv = localStorage.getItem("deviceEnvId"); if (msg.payload && msg.payload.env_id && myEnv && msg.payload.env_id !== myEnv) return; }
    if (msg.type === "__ws_open") { load(); return; }
    if (msg.type === "note_added") {
      if (msg.payload?.incident_id === incidentId) setNotes((n) => (n.some((x) => x.id === msg.payload.id) ? n : [...n, msg.payload]));
      return;
    }
    if (msg.type === "incident_retracted" && msg.payload?.incident_id === incidentId) {
      const mine = msg.payload.scope === "all" || (msg.payload.vehicle_ids || []).includes(vehicle?.id);
      if (mine) setRetracted({ scope: msg.payload.scope });
      else load();
      return;
    }
    if (msg.type === "map_layer_updated") { setSharedLayer(msg.payload && msg.payload.enabled !== false ? msg.payload.drawings : null); return; }
    if (msg.type === "vehicle_broadcast_updated") { loadChannel(); return; }
    if (msg.type === "color_legend_updated") { setLegend((msg.payload && msg.payload.legend) || null); return; }
    if (["photo_added", "incident_updated", "incident_closed", "live_started", "incident_ack", "incident_repaged"].includes(msg.type)) load();
  }, devToken());
  useEffect(() => {
    if (broadcastForMe && liveToken && liveToken !== consentToken && !broadcastOpen) setConsentOpen(true);
  }, [broadcastForMe, liveToken, consentToken, broadcastOpen]);
  useEffect(() => { notesEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [notes]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try { await deviceApi.post(`/incidents/${incidentId}/notes`, { text: note }); setNote(""); }
    catch (err) { toast.error(err.response?.data?.detail || "Mislukt"); }
  };
  const ackIncident = async () => {
    try { await deviceApi.post(`/incidents/${incidentId}/ack`); setAcked(true); toast.success("Uitruk bevestigd"); }
    catch (err) { toast.error(err.response?.data?.detail || "Mislukt"); }
  };
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await deviceApi.post(`/incidents/${incidentId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) { toast.error(err.response?.data?.detail || "Foto mislukt"); }
    }
    if (ok > 0) toast.success(ok === 1 ? "Foto verzonden" : `${ok} foto's verzonden`);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const requestDelete = async (p) => {
    try {
      await deviceApi.post(`/incidents/${incidentId}/photos/${p.id}/request-delete`, {});
      toast.success("Gemarkeerd voor verwijdering. De meldkamer beoordeelt dit.");
      setVlb(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Mislukt"); }
  };

  if (!inc) return <div className="p-6 text-slate-400">Laden…</div>;

  if (retracted) {
    return (
      <div data-testid="vehicle-retracted" className="flex min-h-screen flex-col items-center justify-center bg-slate-800 p-6 text-center text-white">
        <Ban className="mb-6 h-16 w-16 text-amber-400" />
        <div className="rounded-2xl bg-white/10 px-6 py-2 text-lg font-bold uppercase">{retracted.scope === "all" ? "Melding ingetrokken" : "Voertuig ingetrokken"}</div>
        <h1 className="mt-6 font-display text-2xl font-black">{inc.title}</h1>
        <button data-testid="vehicle-retract-back" onClick={onBack} className="mt-8 rounded-2xl bg-white/95 px-8 py-3 text-lg font-bold text-slate-900">Terug naar overzicht</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="vehicle-incident-view">
      <header className="flex items-center gap-3 bg-slate-900 px-4 py-4 text-white">
        <button data-testid="vehicle-back" onClick={onBack} className="rounded-lg bg-white/10 p-2"><ChevronLeft className="h-5 w-5" /></button>
        <div className="min-w-0"><div className="truncate font-display font-bold">{inc.title}</div><div className="font-mono text-xs text-slate-400">{inc.ref}</div></div>
      </header>

      <div className="mx-auto max-w-2xl space-y-4 p-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-2"><PriorityBadge priority={inc.priority} /><span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{inc.incident_type?.name}</span>{inc.subcategory && <span data-testid="vehicle-subcategory" className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-bold text-indigo-700">{inc.subcategory}</span>}</div>
          <div className="mt-2 flex items-center gap-1 text-sm text-slate-600" data-testid="vehicle-building"><Building2 style={{ width: 14, height: 14 }} />{inc.building?.name}{inc.building?.function ? ` · ${inc.building.function}` : ""}</div>
          {inc.description && <p className="mt-2 text-sm text-slate-600">{inc.description}</p>}
        </div>

        <div className="rounded-2xl bg-white p-4 shadow-sm" data-testid="vehicle-alarmed">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Gealarmeerd</div>
          <div className="space-y-1.5" data-testid="vehicle-alarmed-list">
            {(inc.vehicles || []).length === 0 && <div className="text-sm text-slate-400">—</div>}
            {(inc.vehicles || []).map((v) => (
              <div key={v.id} data-testid={`alarmed-vehicle-${v.id}`} className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 ${v.id === vehicle?.id ? "border-blue-300 bg-blue-50" : "border-slate-100 bg-slate-50"}`}>
                <div className="flex min-w-0 items-center gap-2"><Truck style={{ width: 14, height: 14 }} className="shrink-0 text-slate-500" /><span className="truncate font-mono text-sm font-bold text-slate-900">{v.callsign}</span>{v.id === vehicle?.id && <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">JIJ</span>}</div>
                <StatusBadge status={v.status} />
              </div>
            ))}
          </div>
          {ackPolicy.require_vehicle_ack && (inc.acks || []).length > 0 && (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-emerald-600"><CheckCheck style={{ width: 14, height: 14 }} /> Bevestigd door</div>
              <div className="flex flex-wrap gap-1.5">
                {inc.acks.map((a, i) => <span key={`${a.id}-${i}`} className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">{a.name}</span>)}
              </div>
            </div>
          )}
          {ackPolicy.require_vehicle_ack && (
            <button data-testid="vehicle-ack-btn" onClick={ackIncident} disabled={acked}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white hover:bg-emerald-500 disabled:opacity-50">
              <CheckCheck className="h-4 w-4" /> {acked ? "Uitruk bevestigd" : "Bevestig uitruk"}
            </button>
          )}
        </div>

        {(inc.pinned_kb || []).length > 0 && (
          <div className="rounded-2xl bg-white shadow-sm" data-testid="vehicle-procedures">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-900"><BookOpen className="h-4 w-4 text-blue-600" /> Procedures ({inc.pinned_kb.length})</div>
            <div className="space-y-2 p-3">
              {inc.pinned_kb.map((a) => (
                <div key={a.id} data-testid={`vehicle-procedure-${a.id}`} className="rounded-xl border border-slate-200">
                  <button onClick={() => setProcExpanded(procExpanded === a.id ? null : a.id)} className="flex w-full items-start justify-between gap-2 p-3 text-left">
                    <div><div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">{a.title}{(a.attachments || []).length > 0 && <FileText data-testid={`vehicle-proc-pdf-${a.id}`} className="h-3.5 w-3.5 shrink-0 text-red-500" title="Bevat PDF-bijlage" />}</div>{a.summary && <div className="text-xs text-slate-500">{a.summary}</div>}</div>
                    <ChevronRight className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${procExpanded === a.id ? "rotate-90" : ""}`} />
                  </button>
                  {procExpanded === a.id && (
                    <div className="space-y-2 border-t border-slate-100 p-3">
                      {(a.sections || []).map((s, i) => (
                        <div key={i}>
                          <div className="text-[11px] font-bold uppercase tracking-wide text-blue-600">{s.heading}</div>
                          <ul className="mt-0.5 list-disc pl-4 text-sm text-slate-700">{(s.items || []).map((it, j) => <li key={j} className="whitespace-pre-wrap">{it}</li>)}</ul>
                          {(s.images || []).length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-2">
                              {s.images.map((img, k) => <img key={k} src={kbFileUrl(img.path, devToken())} alt="" onClick={() => window.open(kbFileUrl(img.path, devToken()), "_blank")} className="h-24 w-24 cursor-pointer rounded-lg border border-slate-200 object-cover" />)}
                            </div>
                          )}
                        </div>
                      ))}
                      {(a.attachments || []).map((att, k) => (
                        <a key={k} href={kbFileUrl(att.path, devToken())} target="_blank" rel="noreferrer" data-testid={`vehicle-kb-pdf-${a.id}-${k}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm font-semibold text-blue-600"><FileText className="h-4 w-4 shrink-0 text-red-500" /> {att.name || "PDF openen"}</a>
                      ))}
                      {(a.sections || []).length === 0 && (a.attachments || []).length === 0 && <div className="text-xs text-slate-400">Geen stappen beschikbaar.</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {vehicle && (
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Status van {vehicle.callsign}</div>
            <VehicleStatusButton vehicle={vehicle} onChanged={onVehicle} currentIncidentId={incidentId} />
          </div>
        )}

        {liveToken && broadcastForMe && (
          <button data-testid="vehicle-broadcast-btn" onClick={() => setBroadcastOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-red-600 py-4 text-base font-bold text-white shadow">
            <Video className="h-5 w-5" /> Deel je camera met de meldkamer
          </button>
        )}
        {liveToken && !broadcastForMe && (
          <button data-testid="vehicle-live-btn" onClick={() => setLiveOpen(true)}
            className="relative flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-4 text-base font-bold text-white shadow">
            <Radio className="h-5 w-5" /> Bekijk live beeld van incident
            {liveStatus === "active" && <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> LIVE</span>}
          </button>
        )}

        {channelToken && (
          <div className="rounded-2xl bg-white p-4 shadow-sm" data-testid="vehicle-cam-card">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm font-bold text-slate-900"><Video className="h-4 w-4 text-blue-600" /> Voertuigcamera</div>
              {vehBroadcast
                ? <button data-testid="veh-cam-stop-btn" onClick={stopVehBroadcast} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white">Stop delen</button>
                : (bcaster && bcaster.id !== myDeviceId)
                  ? <span data-testid="veh-cam-busy" className="inline-flex items-center gap-1 rounded-lg bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-700"><Video className="h-3.5 w-3.5" /> {bcaster.label || "Andere tablet"} zendt al uit</span>
                  : <button data-testid="veh-cam-share-btn" onClick={startVehBroadcast} className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white">Deel mijn camera</button>}
            </div>
            {vehBroadcast ? (
              <>
                <LiveBroadcast token={channelToken} />
                <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" data-testid="veh-cam-mk-toggle" checked={shareMk} onChange={(e) => toggleShareMk(e.target.checked)} className="h-4 w-4 accent-blue-600" />
                  Ook met meldkamer delen
                </label>
              </>
            ) : (bcaster && bcaster.id !== myDeviceId) ? (
              <>
                <div className="mb-1 text-xs text-slate-500">Live beeld gedeeld door tablet <span className="font-semibold">{bcaster.label || "onbekend"}</span> van dit voertuig.</div>
                <LiveWatch token={channelToken} authToken={devToken()} />
              </>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-4 text-center text-xs text-slate-400">Geen camera actief. Tik op &ldquo;Deel mijn camera&rdquo; om te starten.</div>
            )}
          </div>
        )}

        <BuildingInfo building={inc.building} token={devToken()} />

        {(inc.building || inc.location_lat != null) && (
          <WeatherWidget lat={inc.building ? inc.building.lat : inc.location_lat} lng={inc.building ? inc.building.lng : inc.location_lng} compact />
        )}

        {(inc.building || inc.location_lat != null) && (
          <div className="rounded-2xl bg-white p-3 shadow-sm" data-testid="vehicle-incident-map">
            <div className="mb-2 flex items-center gap-1 text-sm font-bold text-slate-900"><MapPin style={{ width: 15, height: 15 }} /> {mapMode === "bluswater" ? "Bluswater" : "Locatie"}</div>
            <IncidentMiniMap
              key={mapMode}
              lat={inc.building ? inc.building.lat : inc.location_lat}
              lng={inc.building ? inc.building.lng : inc.location_lng}
              label={inc.building ? inc.building.name : (inc.location_label || "Locatie")}
              water={mapMode === "bluswater" ? water : []}
              routes={mapMode === "locatie" && approachEnabled ? ((inc.approach_lines && inc.approach_lines.some((r) => r && r.length)) ? inc.approach_lines : (inc.building?.approach_routes || [])) : []}
              tweezijdig={mapMode === "locatie" && approachEnabled ? !!inc.tweezijdig_aanrijden : false}
              vehiclePos={mapMode === "locatie" ? (pos || (() => { const vb = (inc.vehicles || []).find((v) => v.base_lat != null); return vb ? { lat: vb.base_lat, lng: vb.base_lng } : null; })()) : null}
              drawings={mapMode === "locatie" ? inc.map_drawings : null}
              assignedRoute={mapMode === "locatie" && vehicle && inc.route_assignments ? inc.route_assignments[vehicle.id] : null}
              sharedLayer={mapMode === "locatie" ? sharedLayer : null}
              legend={mapMode === "locatie" ? legend : null}
            />
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button data-testid="map-mode-locatie" onClick={() => setMapMode("locatie")} className={`min-h-[44px] rounded-xl text-sm font-bold ${mapMode === "locatie" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>Locatie</button>
              <button data-testid="map-mode-bluswater" onClick={() => setMapMode("bluswater")} className={`min-h-[44px] rounded-xl text-sm font-bold ${mapMode === "bluswater" ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-700"}`}>💧 Bluswater</button>
            </div>
            {mapMode === "bluswater" && (
              <div className="mt-2 space-y-1" data-testid="vehicle-water-list">
                {water.length === 0 ? <div className="text-xs text-slate-400">Geen bluswaterpunten geregistreerd</div> :
                  water.map((w) => (
                    <div key={w.id} className="flex items-center justify-between rounded-lg bg-sky-50 px-2.5 py-1.5 text-xs">
                      <span className="font-semibold text-slate-800">{w.type === "Blusvijver" ? "🌊" : "💧"} {w.name}</span>
                      <span className="text-slate-500">{w.capacity ? `${w.capacity} l/min` : "—"}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        <div className="rounded-2xl bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-900">Kladblok (meelezen & toevoegen)</div>
          <div className="max-h-72 space-y-2.5 overflow-y-auto p-4">
            {notes.length === 0 && <div className="py-6 text-center text-sm text-slate-400">Nog geen notities</div>}
            {notes.map((n) => (
              <div key={n.id} data-testid={`vnote-${n.id}`} className={`rounded-xl border p-3 transition-colors duration-700 ${flashIds[n.id] ? "border-amber-300 bg-amber-100 ring-2 ring-amber-300" : "border-slate-100 bg-slate-50"}`}>
                <div className="mb-1 flex items-center gap-2"><span className="text-xs font-semibold text-slate-900">{n.author}</span><span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-600">{ROLE_LABELS[n.author_role]}</span><span className="text-[11px] text-slate-400">{timeAgo(n.created_at)}</span></div>
                <div className="whitespace-pre-wrap text-sm text-slate-700">{n.text}</div>
              </div>
            ))}
            <div ref={notesEnd} />
          </div>
          {inc.status === "open" && (
            <form onSubmit={addNote} className="flex items-center gap-2 border-t border-slate-100 p-3">
              <input data-testid="vehicle-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notitie…" className="flex-1 rounded-xl border border-slate-200 px-3 py-3 text-base outline-none focus:border-blue-500" />
              <VoiceRecorder client={deviceApi} size="lg" onTranscript={(t) => setNote((p) => (p ? `${p} ${t}` : t))} />
              <button data-testid="vehicle-send-note" type="submit" className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white"><Send className="h-5 w-5" /></button>
            </form>
          )}
        </div>

        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-bold text-slate-900">Foto's ({photos.length})</div>
            {inc.status === "open" && (<>
              <input ref={fileRef} data-testid="vehicle-photo-input" type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={upload} />
              <button data-testid="vehicle-upload-photo" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white disabled:opacity-60">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Foto's maken
              </button>
            </>)}
          </div>
          {photos.length === 0 ? <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">Nog geen foto's</div> : (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p, idx) => (
                <button key={p.id} type="button" data-testid={`vehicle-photo-${p.id}`} onClick={() => setVlb(idx)} className="relative aspect-square overflow-hidden rounded-lg bg-slate-100">
                  <img src={photoUrl(p.id, devToken())} alt="" className="h-full w-full object-cover" />
                  {p.source === "live_snapshot" && <span className="absolute left-1 top-1 rounded bg-red-600 px-1 py-0.5 text-[8px] font-bold text-white">LIVE</span>}
                  {p.delete_requested && <span data-testid={`vehicle-photo-flag-${p.id}`} className="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded bg-amber-500 px-1 py-0.5 text-[8px] font-bold text-white"><AlertTriangle className="h-2.5 w-2.5" /></span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {consentOpen && (
        <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/80 p-5" data-testid="vehicle-consent-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600"><Video className="h-7 w-7" /></div>
            <h3 className="text-lg font-bold text-slate-900">Live beeld gevraagd</h3>
            <p className="mt-1.5 text-sm text-slate-500">De meldkamer vraagt om live camerabeeld van jouw voertuig. Geef je toestemming om je camera te delen?</p>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button data-testid="consent-decline-btn" onClick={() => { setConsentToken(liveToken); setConsentOpen(false); }}
                className="rounded-xl bg-slate-100 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-200">Weigeren</button>
              <button data-testid="consent-approve-btn" onClick={() => { setConsentToken(liveToken); setConsentOpen(false); setBroadcastOpen(true); }}
                className="rounded-xl bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-500">Toestemming geven</button>
            </div>
          </div>
        </div>
      )}
      {broadcastOpen && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/85 p-4" onClick={() => setBroadcastOpen(false)} data-testid="vehicle-broadcast-modal">
          <button onClick={() => setBroadcastOpen(false)} className="absolute right-4 top-4 rounded-lg bg-white/10 p-2 text-white"><X className="h-5 w-5" /></button>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-white"><Video className="h-4 w-4 text-red-500" /> Deel je camera met de meldkamer</div>
            <LiveBroadcast token={liveToken} audio />
          </div>
        </div>
      )}
      {liveOpen && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/85 p-4" onClick={() => setLiveOpen(false)} data-testid="vehicle-live-modal">
          <button onClick={() => setLiveOpen(false)} className="absolute right-4 top-4 rounded-lg bg-white/10 p-2 text-white"><X className="h-5 w-5" /></button>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-white"><Radio className="h-4 w-4 text-red-500" /> Live beeld van incident</div>
            <LiveWatch token={liveToken} authToken={devToken()} />
          </div>
        </div>
      )}
      {vlb !== null && <Lightbox photos={photos} startIndex={vlb} onClose={() => setVlb(null)} token={devToken()} canFlagDelete={inc.status === "open"} onFlagDelete={requestDelete} />}
    </div>
  );
}
